import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { useBillingPage } from '../hooks/useBillingPage';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { PageLayout } from '../components/layout';

const PaymentSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(15);
  const { data: billingData, isLoading } = useBillingPage();

  useDocumentTitle(t('paymentSuccess.title'));

  // Extract plan information from URL parameters or billing data
  const sessionId = searchParams.get('session_id');
  
  // Get plan details from billing data
  const subscription = billingData?.subscription;
  
  // Try to find the plan name from available plans or fallback to formatted price
  let planName = 'Premium Plan';
  let formattedPrice = '';
  
  if (subscription?.plan) {
    const { amount, currency, interval } = subscription.plan;
    formattedPrice = `${amount / 100} ${currency.toUpperCase()}`;
    
    // Try to find matching plan name from available plans
    const allPlans = [
      ...(billingData?.planOptions?.monthly || []),
      ...(billingData?.planOptions?.yearly || [])
    ];
    
    const matchingPlan = allPlans.find(plan => {
      // Match by price and interval
      const planPrice = parseFloat(plan.formattedPrice.replace(/[^\d.]/g, ''));
      const subscriptionPrice = amount / 100;
      return Math.abs(planPrice - subscriptionPrice) < 0.01; // Allow small floating point differences
    });
    
    if (matchingPlan) {
      planName = matchingPlan.productName;
    } else {
      planName = `${formattedPrice} Plan`;
    }
  }
  
  const billingInterval = subscription?.plan?.interval === 'year' ? 'yearly' : 'monthly';

  // Countdown and redirect logic
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          navigate('/billing');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">{t('paymentSuccess.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-4 lg:pt-0">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="max-w-md w-full text-center">
          {/* Success Animation */}
          <div className="relative mb-8">
            <div className="w-24 h-24 mx-auto rounded-full bg-green-100 flex items-center justify-center animate-pulse">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
            {/* Checkmark animation overlay */}
            <div className="absolute inset-0 w-24 h-24 mx-auto rounded-full border-4 border-green-200 animate-ping"></div>
          </div>

          {/* Success Message */}
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {t('paymentSuccess.successTitle')}
            </h1>
            
            <div className="mb-6">
              <p className="text-lg text-gray-700 mb-2">
                {t('paymentSuccess.successMessage')}
              </p>
              <p className="text-lg text-gray-700">
                {t('paymentSuccess.subscriptionMessage', { 
                  billingInterval: billingInterval === 'yearly' ? t('paymentSuccess.yearly') : t('paymentSuccess.monthly'), 
                  planName 
                })}
              </p>
            </div>

                         {/* Plan Details Card */}
             <div className="bg-primary-50 rounded-lg p-4 mb-6">
               <h3 className="font-semibold text-primary-900 mb-2">{t('paymentSuccess.subscriptionDetails')}</h3>
               <div className="text-sm text-primary-700 space-y-1">
                 <p><span className="font-medium">{t('paymentSuccess.plan')}</span> {planName}</p>
                 {formattedPrice && (
                   <p><span className="font-medium">{t('paymentSuccess.price')}</span> {formattedPrice}</p>
                 )}
                 <p><span className="font-medium">{t('paymentSuccess.billing')}</span> {billingInterval === 'yearly' ? t('paymentSuccess.yearly') : t('paymentSuccess.monthly')}</p>
                 {subscription?.current_period_end && (
                   <p><span className="font-medium">{t('paymentSuccess.nextBilling')}</span> {new Date(subscription.current_period_end * 1000).toLocaleDateString()}</p>
                 )}
               </div>
             </div>

            {/* Countdown and redirect info */}
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                <Trans
                  i18nKey="paymentSuccess.redirectMessage"
                  values={{ countdown }}
                  components={{
                    1: <span className="font-bold text-primary-600" />
                  }}
                />
              </p>
              
              <button
                onClick={() => navigate('/billing')}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                {t('paymentSuccess.goToBilling')}
              </button>
            </div>
          </div>

          {/* Additional info */}
          <div className="mt-6 text-sm text-gray-500">
            <p>{t('paymentSuccess.welcomeMessage')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess; 